import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

// Enhanced debugging for release version
const DEBUG = true;

// Debug logging function
function debugLog(message: string, type: 'info' | 'warn' | 'error' = 'info') {
  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}] [${type.toUpperCase()}] ${message}`;
  console.log(logMessage);
  
  // Also store in global debug logs if available (supports both function + array styles)
  if (typeof window !== 'undefined') {
    const w = window as any;
    try {
      if (typeof w.appDebugLog === 'function') {
        w.appDebugLog(message, type);
        return;
      }
      if (typeof w.debugLog === 'function') {
        w.debugLog(message, type);
        return;
      }
      if (Array.isArray(w.debugLogs)) {
        w.debugLogs.push(logMessage);
        return;
      }
      if (Array.isArray(w.debugLog)) {
        w.debugLog.push(logMessage);
        return;
      }
    } catch {
      // ignore logging failures
    }
  }
}

// Log initialization start with detailed info
debugLog('=== APP INITIALIZATION STARTED ===', 'info');
debugLog('User agent: ' + navigator.userAgent, 'info');
debugLog('Window location: ' + window.location.href, 'info');
debugLog('Document ready state: ' + document.readyState, 'info');
debugLog('Capacitor available: ' + (typeof (window as any).Capacitor !== 'undefined'), 'info');
debugLog('Performance timing available: ' + (typeof performance !== 'undefined'), 'info');

// Import Firebase and CSS - these are needed by the App
// Firebase initialization is non-blocking (errors are caught internally)
import './firebase';
import './index.css';

// Initialize app immediately - don't wait for anything
const initApp = () => {
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    debugLog('Starting React app initialization...', 'info');
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error("Could not find root element to mount to");
    }

    const time1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    debugLog(`Root element found (${(time1 - startTime).toFixed(2)}ms), creating React root...`, 'info');
    const root = ReactDOM.createRoot(rootElement);
    
    const time2 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    debugLog(`React root created (${(time2 - startTime).toFixed(2)}ms), rendering App component...`, 'info');
    
    debugLog('About to call root.render()...', 'info');
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    
    const renderTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    debugLog(`App render() called successfully in ${(renderTime - startTime).toFixed(2)}ms`, 'info');
    
    // Dispatch event to indicate app initialized
    setTimeout(() => {
      window.dispatchEvent(new Event('app-initialized'));
      debugLog('App initialized event dispatched', 'info');
    }, 100);
  } catch (error) {
    debugLog('Failed to initialize React app: ' + (error instanceof Error ? error.message : String(error)), 'error');
    debugLog('Error stack: ' + (error instanceof Error ? error.stack : 'No stack trace'), 'error');
    console.error('Full error object:', error);
    
    // Show error message to user
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; text-align: center; font-family: sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; background: #F8F9FA;">
          <h1 style="color: #1F2937; margin-bottom: 16px; font-size: 24px;">Application Error</h1>
          <p style="color: #4B5563; margin-bottom: 8px; font-size: 16px;">The application failed to start.</p>
          <p style="color: #9CA3AF; font-size: 12px; margin-top: 16px; max-width: 400px; word-break: break-word;">${error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onclick="window.location.reload()" style="margin-top: 20px; padding: 12px 24px; background: #FF6000; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px;">
            Reload App
          </button>
          <p style="color: #9CA3AF; font-size: 10px; margin-top: 20px;">Check console for debug logs</p>
          <details style="margin-top: 20px; text-align: left; max-width: 400px;">
            <summary style="cursor: pointer; color: #666;">Debug Logs</summary>
            <pre style="background: #f3f4f6; padding: 10px; border-radius: 4px; font-size: 10px; overflow: auto; max-height: 200px;">${typeof window !== 'undefined' && (window as any).debugLog ? (window as any).debugLog.join('\n') : 'No debug logs available'}</pre>
          </details>
        </div>
      `;
    }
  }
};

// Initialize immediately - don't wait for DOMContentLoaded
// The root element already exists in the HTML
debugLog('DOM ready state: ' + document.readyState, 'info');
debugLog('Initializing app immediately...', 'info');
initApp();

// Enhanced global error handler
window.addEventListener('error', (event) => {
  debugLog('Global error caught: ' + event.message, 'error');
  debugLog('Error filename: ' + event.filename + ':' + event.lineno + ':' + event.colno, 'error');
  console.error('Full error details:', {
    error: event.error,
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack
  });
});

window.addEventListener('unhandledrejection', (event) => {
  debugLog('Unhandled promise rejection: ' + (event.reason?.message || String(event.reason)), 'error');
  console.error('Unhandled promise rejection details:', {
    reason: event.reason,
    stack: event.reason?.stack
  });
});
